import React from 'react';
import { useForm } from 'react-hook-form';
import { X, Save, MapPin, Zap, Shield, Building2, DollarSign, Globe, Wind, Server } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../hooks/useAuth';
import { addLocationToUser } from '../../../utils/userService';
import { toast } from 'react-hot-toast';

interface AddLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PREDEFINED_FEATURES = [
  '24/7 Security',
  'Redundant Power',
  'Advanced Cooling',
  'Remote Management',
  'Technical Support',
  'Backup Generators',
  'Fire Suppression',
  'Biometric Access'
];

interface LocationFormData {
  name: string;
  location: string;
  country: string;
  capacity: string;
  powerSource: string;
  powerCost: number;
  minOrder: number;
  securityLevel: 'standard' | 'advanced' | 'enterprise';
  selectedFeatures: string[];
  customFeatures: string;
  description: string;
  isPublished: boolean;
}

export function AddLocationModal({ isOpen, onClose }: AddLocationModalProps) {
  const { user } = useAuth();
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<LocationFormData>({
    defaultValues: {
      selectedFeatures: [],
      customFeatures: '',
      isPublished: false,
      securityLevel: 'standard'
    }
  });

  const selectedFeatures = watch('selectedFeatures', []);

  const onSubmit = async (data: LocationFormData) => {
    if (!user) return;

    try {
      // Combine selected features with custom features
      const customFeaturesList = data.customFeatures
        .split('\n')
        .filter(feature => feature.trim().length > 0);
      
      const allFeatures = [...selectedFeatures, ...customFeaturesList];

      // Create location document
      const locationData = {
        ...data,
        features: allFeatures,
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: user.uid,
        status: 'active' as const,
        isPublished: false // Always start as draft
      };

      await addLocationToUser(user.uid, locationData);
      toast.success('Location added successfully');
      onClose();
      
      // Force refresh of the locations list
      window.location.reload();
    } catch (error) {
      console.error('Error adding location:', error);
      toast.error('Failed to add location');
    }
  };

  const handleFeatureToggle = (feature: string) => {
    const current = selectedFeatures;
    const updated = current.includes(feature)
      ? current.filter(f => f !== feature)
      : [...current, feature];
    setValue('selectedFeatures', updated);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl mx-auto"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Location
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Basic Information
                </h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <input
                    type="text"
                    {...register('location', { required: 'Location is required' })}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Country</label>
                  <input
                    type="text"
                    {...register('country', { required: 'Country is required' })}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
                  )}
                </div>
              </div>

              {/* Capacity & Power */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Capacity & Power
                </h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Capacity (MW)</label>
                  <input
                    type="text"
                    {...register('capacity', { required: 'Capacity is required' })}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.capacity && (
                    <p className="mt-1 text-sm text-red-600">{errors.capacity.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Power Cost ($/kWh)</label>
                  <input
                    type="number"
                    step="0.001"
                    {...register('powerCost', { required: 'Power cost is required' })}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.powerCost && (
                    <p className="mt-1 text-sm text-red-600">{errors.powerCost.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Security Level</label>
                  <select
                    {...register('securityLevel')}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="standard">Standard</option>
                    <option value="advanced">Advanced</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-4 md:col-span-2">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Features
                </h4>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {PREDEFINED_FEATURES.map((feature) => (
                    <label
                      key={feature}
                      className="relative flex items-start"
                    >
                      <div className="flex items-center h-5">
                        <input
                          type="checkbox"
                          checked={selectedFeatures.includes(feature)}
                          onChange={() => handleFeatureToggle(feature)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    </label>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Additional Features (one per line)
                  </label>
                  <textarea
                    {...register('customFeatures')}
                    rows={3}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter custom features..."
                  />
                </div>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  {...register('description')}
                  rows={4}
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your facility..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700"
              >
                <Save className="w-4 h-4 inline-block mr-2" />
                Add Location
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}